import { ActionButton, getNextTypography, makeStyles } from '@paintscout/ui'
import type { Action, MuiTheme as Theme } from '@paintscout/ui'
import type { QuoteItemSection, RenderableItem } from '@paintscout/util/builder'
import classnames from 'classnames'
import React from 'react'
import { ItemColumn } from '../ItemTable'

export interface ItemRowContentProps {
  item: RenderableItem
  section: QuoteItemSection
  isEditable: boolean
  showAmount: boolean
  consumer?: 'crew' | 'customer'
  /** Shrinks text to the Work Order's smaller type scale (matches WorkOrderCardNext). */
  compact?: boolean
  /** Kebab actions; when omitted (or empty) the kebab is hidden. */
  actions?: Action[]
  onAction?: (action: string, item: RenderableItem) => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const type = getNextTypography(theme)
    return {
      // ItemColumn (title/description + right-aligned price) fills the row; the kebab sits at the far
      // right, top-aligned with the title line.
      root: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(2)
      },
      itemColumn: {
        flex: 1,
        minWidth: 0
      },
      // The estimate's type roles (ENG-2094) laid over ItemColumn, which the classic editor shares so
      // cannot change itself: the name line and its price at 15px, the description and sub-items at
      // 14/400. The px sizes replace the font-scaled body1 that the presentation styles and MUI's
      // Typography put on <p>/<span>; `&&` outranks those.
      itemColumnEstimate: {
        ...type.body,
        '&& p, && ul, && ol, && span': {
          fontSize: 'inherit'
        },
        // ItemColumn's first row: the name (bold when it is a name) and the price.
        '& > div > div:first-child': {
          fontSize: type.rowTitle.fontSize
        }
      },
      actionColumn: {
        flexShrink: 0,
        marginTop: -8,
        '& svg': {
          color: theme.paintscout.palette.brand.Neutral.P60
        },
        '@media print': {
          display: 'none'
        }
      },
      // The Work Order's 14px type runs a shorter first line than the estimate's, so the -8 above
      // left the dots ~2px below the title's glyphs there.
      actionColumnCompact: {
        marginTop: -10
      }
    }
  },
  { name: 'ItemRowContent' }
)

/**
 * Shared row content for every outlined item table (Item / Options / Additional / Pending / …) in
 * the new (estimateNext) pipeline: the reused `ItemColumn` plus a top-aligned, subdued kebab. Keeps
 * the row layout identical across sections so they read as one system.
 */
export default function ItemRowContent({
  item,
  section,
  isEditable,
  showAmount,
  consumer = 'customer',
  compact,
  actions,
  onAction
}: ItemRowContentProps) {
  const classes = useStyles()
  return (
    <div className={classes.root}>
      <div className={classnames(classes.itemColumn, { [classes.itemColumnEstimate]: !compact })}>
        <ItemColumn
          item={item}
          section={section}
          isEditable={isEditable}
          showAmount={showAmount}
          consumer={consumer}
          compact={compact}
        />
      </div>
      {actions && actions.length > 0 && onAction && (
        <div className={classnames(classes.actionColumn, { [classes.actionColumnCompact]: compact }, 'no-drag')}>
          <ActionButton nextMenu actions={actions} onActionClick={(_ev, action) => onAction(action, item)} />
        </div>
      )}
    </div>
  )
}
